import React, { useState, useEffect } from 'react';
import { NormalBreadcrumb } from '@/components/Breadcrumb';
import { useRequest } from 'ahooks';
import { getDeviceMaps } from '@/api/device';
import { Form, Select, Tabs } from 'antd';
import { getDevice } from '@/api';
import { useSearchParams } from 'react-router-dom';
import Map3D from './components/Map3D';
import MapCluster from './components/MapCluster';
import AMapView from './components/AMapView';

const DeviceMap = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [filterValues, setFilterValues] = useState<{ name?: string; status?: string }>({});
  
  // 获取所有数据，不传递筛选参数
  const { data } = useRequest(getDeviceMaps, {
    defaultParams: [{}],
  });
  
  // 从 URL 参数读取 tab，如果没有则默认为 'amap'
  const tabFromUrl = searchParams.get('tab') || 'amap';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  // 当 URL 参数变化时，更新 activeTab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['amap', '3d', 'cluster'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const { data: deviceData } = useRequest(getDevice, { defaultParams: [{ isAll: true }] });
  
  // 本地筛选后的原始数据
  const filteredResult = React.useMemo(() => {
    if (!data?.result) {
      return [];
    }
    
    let result = [...data.result];
    
    // 按设备名称筛选
    if (filterValues.name) {
      result = result.filter((item) => item.name === filterValues.name);
    }
    
    // 按设备状态筛选
    if (filterValues.status) {
      result = result.filter((item) => item.status === filterValues.status);
    }
    
    return result;
  }, [data?.result, filterValues]);
  
  // 转换数据格式供 L7 地图使用
  const mapData = React.useMemo(() => {
    if (!filteredResult || filteredResult.length === 0) {
      console.log('DeviceMap - 没有数据');
      return [];
    }

    const result = filteredResult
      .map((item) => {
        let lng = Number(item.lon);
        let lat = Number(item.lat);

        // 如果坐标无效，使用默认位置（中国中心点）
        if (isNaN(lng) || isNaN(lat) || lng === 0 || lat === 0) {
          // 默认位置：北京（可根据实际情况调整）
          lng = 116.397428;
          lat = 39.90923;
        }
        return {
          lon: lng,            // 数字格式，AMapView 内部会 Number() 转换
          lat: lat,
          lng: lng,
          name: item.name || '',
          status: item.status || '',
        };
      });

    console.log('DeviceMap - API data:', data);
    console.log('DeviceMap - filteredResult:', filteredResult);
    console.log('DeviceMap - mapData:', result);
    return result;
  }, [filteredResult, data]);
  
  // 处理筛选条件变化（本地筛选，不调用 API）
  const handleFilterChange = (changedValues: any, allValues: any) => {
    const filters: { name?: string; status?: string } = {};
    if (allValues.name) {
      filters.name = allValues.name;
    }
    if (allValues.status) {
      filters.status = allValues.status;
    }
    // 更新筛选条件，触发本地筛选
    setFilterValues(filters);
  };

  return (
    <>
      <style>{`
        .device-map-tabs .ant-tabs-nav {
          margin: 0 !important;
          height: 22px;
          display: flex !important;
          align-items: center !important;
        }
        .device-map-tabs .ant-tabs-tab {
          padding: 0 12px !important;
          height: 22px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .device-map-tabs .ant-tabs-tab-btn {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .device-map-tabs .ant-tabs-content-holder {
          display: none;
        }
        .device-map-tabs .ant-tabs-nav-list {
          height: 22px;
          display: flex !important;
          align-items: center !important;
        }
      `}</style>
      {/* 导航条和Tab容器 - 使用flex布局，但不改变导航条的margin */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {/* 导航条 - 保持原有位置和样式，不做任何修改 */}
        <NormalBreadcrumb></NormalBreadcrumb>
        {/* Tab - 绝对定位，与导航条同一水平线，居中显示 */}
        <div className="device-map-tabs" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', backgroundColor: 'transparent', zIndex: 999 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="small"
            items={[
              {
                key: 'amap',
                label: '高德地图',
              },
              {
                key: '3d',
                label: '3D地图',
              },
              {
                key: 'cluster',
                label: '聚合地图',
              },
            ]}
          />
        </div>
      </div>
      <div style={{ position: 'relative', width: '100%', height: 'calc(100vh - 64px)', marginTop: '-20px' }}>
        {/* 筛选表单 - 绝对定位在地图上方 */}
        <div style={{ position: 'absolute', zIndex: 1000, right: 20, top: 20, backgroundColor: 'transparent', padding: '8px', borderRadius: '4px' }}>
          <Form
            form={form}
            onValuesChange={handleFilterChange}
          >
            <Form.Item name="name">
              <Select
                options={deviceData?.result?.list || []}
                allowClear
                showSearch
                optionFilterProp="name"
                placeholder="设备名称"
                style={{ width: 120 }}
                fieldNames={{ label: 'name', value: 'name' }}
              ></Select>
            </Form.Item>
            <Form.Item name="status">
              <Select
                options={[
                  { label: '在线', value: '在线' },
                  { label: '离线', value: '离线' },
                ]}
                style={{ width: '120px' }}
                placeholder="设备状态"
                allowClear
              ></Select>
            </Form.Item>
          </Form>
        </div>
        {/* 地图容器 - 铺满整个窗口 */}
        <div style={{ width: '100%', height: '100%' ,marginTop: '20px'}}>
          {activeTab === 'amap' && <AMapView data={mapData} />}
          {activeTab === '3d' && <Map3D data={mapData} />}
          {activeTab === 'cluster' && <MapCluster data={mapData} />}
        </div>
      </div>
    </>
  );
};

export default DeviceMap;
